import { z } from 'zod';
import {
  BULK_BATCH_STATES,
  BULK_INPUT_TYPES,
  BULK_OPERATIONS,
  CHANGE_PROCESS_TYPES,
  CHANGE_REASON_STATUSES,
  DEVICE_STATUSES,
  DEVICE_TYPES,
  PHONE_TYPES,
  SIM_STATUSES,
  US_STATE_CODES,
  UNIT_KINDS,
  USER_LEVELS,
} from '../enums';
import type { BulkOperation, UnitKind, UnitStatus, UserLevel } from '../enums';
import { digits, listQuerySchema, objectId, optionalText, queryBool, recordStatus } from './common';
import { SHIPPERS } from './screens';
import type { UserRef } from './users';

/**
 * Inventory (reference PC345, PC314, PC732, PC347). Field lists come from the reference
 * "Add Unit" form, the unit list and the Manage Model column chooser;
 * see docs/product-inventory.md appendix parts 2 and 4.
 */

/* ----------------------------- Product models ----------------------------- */

/**
 * Descriptive columns of the reference's Model List (its column chooser, 45 columns) that
 * hold typed text: key, column header, longest value. Only the column names were read on
 * the reference; the "Update Model Details" dialog was not, so every one is plain text here.
 */
export const MODEL_TEXT_FIELDS = [
  ['insurance', 'Insurance', 40],
  ['emi', 'EMI', 40],
  ['planType', 'Plan Type', 40],
  ['featuresDescription', 'Features description', 1000],
  ['specification', 'Specification', 1000],
  ['keyBenefits', 'Key Benefits', 1000],
  ['productDetails', 'Product Details', 1000],
  ['fccId', 'FCC ID', 40],
  ['hac', 'HAC', 40],
  ['wifi', 'WiFi', 40],
  ['dataSupport', 'Data', 40],
  ['mRating', 'M Rating', 20],
  ['tRating', 'T Rating', 20],
  ['voiceOnly', 'Voice Only', 20],
  ['hotspotCapable', 'Hotspot Capable', 20],
  ['bluetooth', 'Bluetooth', 40],
  ['versionName', 'Version', 40],
  ['talkTime', 'Talk Time', 40],
  ['imeiType', 'IMEI TYPE', 40],
  ['discount', 'Discount', 20],
  ['discountValidity', 'Discount Validity', 40],
] as const;
export type ModelTextKey = (typeof MODEL_TEXT_FIELDS)[number][0];

const modelText = (max: number) => z.string().trim().max(max).optional().default('');
const modelTextShape = Object.fromEntries(
  MODEL_TEXT_FIELDS.map(([key, , max]) => [key, modelText(max)]),
) as Record<ModelTextKey, ReturnType<typeof modelText>>;

/** "Phone Capable" of the reference's Update Model Details (walk of 2026-10-27). */
export const PHONE_CAPABILITIES = ['2G', '3G', '4G', '4GLTE', 'LTE', '5G'] as const;
/** "Client Type": the kind of customer the model is for. */
export const MODEL_CLIENT_TYPES = ['ACP', 'Prepaid', 'Both'] as const;

/**
 * Fields of Update Model Details added with the switch `model-details-2`. They describe the
 * model; nothing is charged from Activation Fees or Co-pay Amount, and Handover, E-SIM
 * Compatible, Client Type and Position change no flow (assumption: what they drive on the
 * reference was not seen).
 */
const modelDetailsShape = {
  phoneCapable: z.enum(PHONE_CAPABILITIES).nullable().default(null),
  handover: z.boolean().default(false),
  esimCompatible: z.boolean().default(false),
  clientType: z.enum(MODEL_CLIENT_TYPES).nullable().default(null),
  position: z.number().int().min(0).max(9999).nullable().default(null),
  activationFeeCents: z.number().int().min(0).max(10_000_000).default(0),
  copayCents: z.number().int().min(0).max(10_000_000).default(0),
  shortDescription: z.string().trim().max(250).optional().default(''),
  noteDescription: z.string().trim().max(2000).optional().default(''),
};
export const MODEL_DETAIL_KEYS = Object.keys(modelDetailsShape) as Array<
  keyof typeof modelDetailsShape
>;

export const productModelInputSchema = z.object({
  make: z.string().trim().min(1, 'Make is required').max(60),
  model: z.string().trim().min(1, 'Model is required').max(80),
  modelNumber: z.string().trim().max(60).optional().default(''),
  sku: z.string().trim().max(40).optional().default(''),
  /** True for SIM products, which have no device attributes. */
  isSim: z.boolean().default(false),
  deviceType: z.enum(DEVICE_TYPES).nullable().default(null),
  phoneType: z.enum(PHONE_TYPES).nullable().default(null),
  networkType: z.string().trim().max(30).optional().default(''),
  simType: z.string().trim().max(30).optional().default(''),
  os: z.string().trim().max(40).optional().default(''),
  color: z.string().trim().max(30).optional().default(''),
  grade: z.string().trim().max(20).optional().default(''),
  amountCents: z.number().int().min(0).default(0),
  inventoryThreshold: z.number().int().min(0).nullable().default(null),
  showOnWebsite: z.boolean().default(false),
  ram: z.string().trim().max(20).optional().default(''),
  storage: z.string().trim().max(20).optional().default(''),
  screen: z.string().trim().max(30).optional().default(''),
  camera: z.string().trim().max(40).optional().default(''),
  /** The "Carrier" column: the carrier this model is sold for, if any. */
  carrierId: objectId.nullable().default(null),
  /** "CSR Equipment Phone Type" and "Website Equipment Phone Type": the list filters are Y/N. */
  csrEquipmentPhoneType: z.boolean().default(false),
  websiteEquipmentPhoneType: z.boolean().default(false),
  ...modelTextShape,
  ...modelDetailsShape,
  status: recordStatus.default('Active'),
});
export type ProductModelInput = z.input<typeof productModelInputSchema>;
export const productModelUpdateSchema = productModelInputSchema.partial();

export const productModelListQuerySchema = listQuerySchema.extend({
  q: optionalText(),
  status: recordStatus.optional(),
  deviceType: z.enum(DEVICE_TYPES).optional(),
  isSim: queryBool,
  /** "Model ID" filter: the Make Model ID. */
  modelId: z.coerce.number().int().positive().optional(),
  csr: queryBool,
  website: queryBool,
});

/** Pictures of the catalogue (Image 1 to 3 of a model, Image 1 of an equipment). */
export const CATALOGUE_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif'] as const;
/** "max 2 MB" on the reference's equipment form. */
export const CATALOGUE_IMAGE_MAX_BYTES = 2 * 1024 * 1024;
export const MODEL_IMAGE_SLOTS = [1, 2, 3] as const;
export interface CatalogueImageRef {
  slot: number;
  /** Changes with each new picture; added to the picture's address so a browser fetches it again. */
  updatedAt: string;
}

export interface ProductModelDto extends z.output<typeof productModelInputSchema> {
  id: string;
  /** Image 1 to 3 that are set. */
  images: CatalogueImageRef[];
  /** Sequential "Make Model ID". */
  modelId: number;
  /** Name of the carrier in `carrierId`. */
  carrier: string;
  /** Units of this model currently in stock (any status). */
  inventory: number;
  createdAt: string;
  updatedAt: string;
  createdBy: UserRef | null;
  updatedBy: UserRef | null;
}

/* ------------------------------ Change reasons ---------------------------- */

export const changeReasonInputSchema = z.object({
  name: z.string().trim().min(1, 'Reason is required').max(100),
  /** Status the old SIM or device takes when this reason is used. */
  reasonStatus: z.enum(CHANGE_REASON_STATUSES),
  processType: z.enum(CHANGE_PROCESS_TYPES).default('BOTH'),
  status: recordStatus.default('Active'),
});
export type ChangeReasonInput = z.input<typeof changeReasonInputSchema>;
export const changeReasonUpdateSchema = changeReasonInputSchema.partial();

export const changeReasonListQuerySchema = listQuerySchema.extend({
  q: optionalText(),
  reasonStatus: z.enum(CHANGE_REASON_STATUSES).optional(),
  status: recordStatus.optional(),
  processType: z.enum(CHANGE_PROCESS_TYPES).optional(),
});

export interface ChangeReasonDto extends z.output<typeof changeReasonInputSchema> {
  id: string;
  createdAt: string;
  updatedAt: string;
  createdBy: UserRef | null;
  updatedBy: UserRef | null;
}

/* ----------------------------- Inventory units ---------------------------- */

/**
 * Identifier formats are assumptions (the reference form showed no length limits):
 * SIM/ICCID 18 to 22 digits, IMEI 14 to 16 digits, MDN 10 digits, ZIP 5 digits.
 */
export const simNumber = digits(18, 22, 'SIM');
export const imeiNumber = digits(14, 16, 'IMEI');
const emptyToUndefined = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? undefined : v);

/** "Add Single Unit". At least one of SIM or IMEI; both creates a linked pair. */
export const unitCreateSchema = z
  .object({
    carrierId: objectId,
    sim: z.preprocess(emptyToUndefined, simNumber.optional()),
    imei: z.preprocess(emptyToUndefined, imeiNumber.optional()),
    isEsim: z.boolean().default(false),
    /** "Assign to user": the owner in the agent hierarchy. Absent = unassigned stock. */
    ownerUserId: objectId.nullable().default(null),
    /** "Product Name". */
    modelId: objectId,
    zip: z.preprocess(emptyToUndefined, digits(5, 5, 'Zipcode').optional()),
    puk2: z.string().trim().max(20).optional().default(''),
    poNumber: z.string().trim().max(50).optional().default(''),
    reservedForPortin: z.boolean().default(false),
    planId: objectId.nullable().default(null),
    /** "Telephone Number". */
    mdn: z.preprocess(emptyToUndefined, digits(10, 10, 'Telephone Number').optional()),
  })
  .superRefine((v, ctx) => {
    if (!v.sim && !v.imei)
      ctx.addIssue({ code: 'custom', path: ['sim'], message: 'Enter a SIM, an IMEI, or both' });
    if (v.isEsim && !v.sim)
      ctx.addIssue({ code: 'custom', path: ['isEsim'], message: 'eSIM needs a SIM number' });
  });
export type UnitCreateInput = z.input<typeof unitCreateSchema>;

/** "Update Single Unit". Status changes need a change reason unless the unit returns to Free. */
export const unitUpdateSchema = z.object({
  ownerUserId: objectId.nullable().optional(),
  modelId: objectId.optional(),
  status: z.enum(SIM_STATUSES).optional(),
  reasonId: objectId.nullable().optional(),
  zip: z.preprocess(emptyToUndefined, digits(5, 5, 'Zipcode').optional()),
  puk2: z.string().trim().max(20).optional(),
  poNumber: z.string().trim().max(50).optional(),
  reservedForPortin: z.boolean().optional(),
  planId: objectId.nullable().optional(),
});
export type UnitUpdateInput = z.input<typeof unitUpdateSchema>;

export const unitListQuerySchema = listQuerySchema.extend({
  pageSize: z.coerce.number().int().min(1).max(200).default(15),
  kind: z.enum(UNIT_KINDS).optional(),
  carrierId: objectId.optional(),
  status: z.enum(SIM_STATUSES).optional(),
  /** true = eSIM only, false = physical SIM only. */
  esim: queryBool,
  modelId: objectId.optional(),
  /** Free text over SIM, IMEI, MDN and store (owner login ID). */
  q: optionalText(),
  /** Exact SIM or device number: "Search by SIM" / "Search by Device ID" of Adjustments. */
  identifier: optionalText(),
  /** Exact box number: "Search by Box Number" of Adjustments. */
  box: optionalText(),
});
export type UnitListQuery = z.input<typeof unitListQuerySchema>;

export interface UnitDto {
  id: string;
  kind: UnitKind;
  sim: string | null;
  imei: string | null;
  isEsim: boolean;
  /** Identifier of the linked SIM or device when the unit was added as a pair. */
  paired: { id: string; kind: UnitKind; identifier: string } | null;
  carrier: { id: string; code: string; name: string };
  model: { id: string; make: string; model: string };
  status: UnitStatus;
  /** "Store Type" and "Store ID" on the reference list. */
  owner: (UserRef & { level: UserLevel }) | null;
  mdn: string | null;
  zip: string | null;
  puk2: string;
  poNumber: string;
  reservedForPortin: boolean;
  plan: { id: string; name: string } | null;
  uploadedAt: string;
  uploadedBy: UserRef | null;
  /** The fields of Update Inventory (PC391). */
  details: UnitDetailsDto;
}

export interface UnitDetailsDto {
  msid: string;
  box: string;
  notes: string;
  uiccid: string;
  byod: boolean | null;
  wholesalePriceCents: number | null;
  sellingPriceCents: number | null;
  activationFeeCents: number | null;
  retailPriceCents: number | null;
  acpCoPayCents: number | null;
  acpReimbursementCents: number | null;
}

export interface InventorySummary {
  sim: Record<'total' | 'available' | 'used' | 'bad' | 'removed' | 'esim' | 'physical', number>;
  device: Record<'total' | 'available' | 'used' | 'bad' | 'removed', number>;
  /** SIMs added per day, last 30 days. */
  simTrend: Array<{ date: string; count: number }>;
  simByCarrier: Array<{ carrier: string; count: number }>;
  topProducts: Array<{ product: string; count: number }>;
}

/** Kinds of line of "SIM & Device Add/Update Activity" (the reference colours each). */
export const INVENTORY_ACTIVITY_TYPES = ['sim', 'device', 'bad', 'removed', 'update'] as const;

/** One line of "SIM & Device Add/Update Activity" on the Full Inventory Dashboard. */
export interface InventoryActivityItem {
  type: (typeof INVENTORY_ACTIVITY_TYPES)[number];
  /** "SIM added: 8901...", as the reference words it. */
  text: string;
  at: string;
}

/**
 * "Inventory Health" of the Full Inventory Dashboard. Assumptions (the reference's sources were
 * not seen): accuracy is the share of units whose status agrees with the customers (a Used unit
 * held by a customer, any other unit held by none); pending actions are bulk uploads not finished;
 * today's transactions are the unit history lines of the day.
 */
export interface InventoryHealth {
  accuracyPercent: number;
  pendingActions: number;
  todayTransactions: number;
}

export interface InventoryFullDashboard {
  activity: InventoryActivityItem[];
  health: InventoryHealth;
}

/** The three "count" tabs of the new Inventory Dashboard (walk of 2026-10-15). */
export const INVENTORY_COUNT_VIEWS = ['product', 'user', 'age'] as const;
export type InventoryCountView = (typeof INVENTORY_COUNT_VIEWS)[number];
export const inventoryCountQuerySchema = z.object({ by: z.enum(INVENTORY_COUNT_VIEWS) });
/**
 * Age bands of "SIM Count by Age". The reference showed "12-24 months old" and "24+ months
 * old" (the bands that had SIMs); the three younger bands are this project's.
 */
export const SIM_AGE_BANDS = [
  '0-3 months old',
  '3-6 months old',
  '6-12 months old',
  '12-24 months old',
  '24+ months old',
] as const;

/** One line of a count tab; the columns depend on the tab. */
export interface InventoryCountRow {
  /** Product: the model number; user: the login ID; age: the band. */
  key: string;
  label: string;
  /** User: the level of the user who uploaded the SIMs. */
  userType?: string;
  available: number;
  used: number;
  bad: number;
  removed: number;
  /** User: SIMs of lines that were disconnected. */
  disconnected?: number;
  total: number;
}

export interface InventoryEventDto {
  id: string;
  type: 'added' | 'updated' | 'reassigned' | 'statusChanged' | 'assigned' | 'cleared';
  from: Record<string, unknown> | null;
  to: Record<string, unknown> | null;
  reason: string | null;
  batchId: string | null;
  actor: UserRef | null;
  at: string;
}

/* ------------------------------ Bulk batches ------------------------------ */

/** Target owner for reassignment, chosen with the cascading Master/Distributor/Retailer/Employee selects. */
const bulkOwner = z.object({ ownerUserId: objectId });

/* -------------------------- Manage Shipper Inventory ------------------------ */

/** "ESN/SIM Type" of Manage Shipper Inventory (Dropship Orders), in the reference's order. */
export const SHIPPER_ESN_TYPES = [
  'Free Phone With Data',
  'Free Phone Without Data',
  'Paid Phone',
  'Paid Phone NonLL',
  'Post Paid Phone NonLL',
  'Sim Only',
  'Call Centers',
  'Fast Track',
  'Split Orders',
  'BYOD Orders',
  'Family Plan',
  'Tribal Orders',
  'Non-Tribal Orders',
  'Overnight Shipping Orders',
  'ACP Order',
  'ACP BYOD Order',
  'ACP SIM Order',
  'ACP Phone Order',
  'ACP Hotspot Order',
] as const;
/** "Include Orders". */
export const SHIPPER_INCLUDE_ORDERS = [
  'Home Delivery',
  'Retailer Location',
  'Event(Take Away)',
  'Shipment',
] as const;

const blankToNone = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? undefined : v);

/** What a shipper's stock is held for; kept on the SIM. */
export interface ShipperStockDto {
  esnType: (typeof SHIPPER_ESN_TYPES)[number];
  includeOrders: (typeof SHIPPER_INCLUDE_ORDERS)[number];
  state: string;
  shipper: string;
  phoneType: (typeof PHONE_TYPES)[number];
  areaCode: string;
}

/**
 * "Manage Shipper Inventory", Single Unit (release switch `shipper-inventory`). Shipper ID is the
 * shipper's name here (212Ez keeps no shipper accounts with numbers); Enrollment ID, UICCID, Area
 * Code, Device and Device Model are optional.
 */
export const shipperUnitSchema = z
  .object({
    esnType: z.enum(SHIPPER_ESN_TYPES, { required_error: 'Select ESN/SIM Type' }),
    includeOrders: z.enum(SHIPPER_INCLUDE_ORDERS, { required_error: 'Select Include Orders' }),
    enrollmentId: z.string().trim().max(25).default(''),
    state: z.enum(US_STATE_CODES, { required_error: 'State is required' }),
    sim: simNumber,
    simModelId: z.string().regex(/^[a-f\d]{24}$/i, 'Select ESN/SIM Model'),
    shipper: z.enum(SHIPPERS, { required_error: 'Select Shipper ID' }),
    carrierId: z.string().regex(/^[a-f\d]{24}$/i, 'Select Carrier'),
    uiccid: z.string().trim().max(25).default(''),
    areaCode: z.preprocess(blankToNone, digits(3, 3, 'Area Code').optional()),
    device: z.preprocess(blankToNone, imeiNumber.optional()),
    deviceModelId: z.preprocess(blankToNone, objectId.optional()),
    phoneType: z.enum(PHONE_TYPES, { required_error: 'Select Phone Type' }),
  })
  .superRefine((v, ctx) => {
    if (v.device && !v.deviceModelId)
      ctx.addIssue({ code: 'custom', path: ['deviceModelId'], message: 'Select Device Model' });
  });
export type ShipperUnitInput = z.input<typeof shipperUnitSchema>;

export const bulkParamsSchemas = {
  'add-units': z.object({}),
  'reassign-sim': bulkOwner,
  'sim-status-reassign': z.object({
    status: z.enum(SIM_STATUSES),
    ownerUserId: objectId.nullable().default(null),
    reasonId: objectId.nullable().default(null),
  }),
  'reassign-device': bulkOwner,
  'device-status-reassign': z.object({
    status: z.enum(DEVICE_STATUSES),
    ownerUserId: objectId.nullable().default(null),
    reasonId: objectId.nullable().default(null),
  }),
  'clear-device': z.object({}),
  'clear-sim': z.object({}),
  'reassign-sim-by-agent': bulkOwner,
  'reassign-device-by-agent': bulkOwner,
  'line-status': z.object({}),
  // Type - 1 fields of "Deactivate MDN"; a file row may carry its own values instead.
  'deactivate-mdn': z.object({
    reasonId: objectId.nullable().default(null),
    bssDisconnect: z.enum(['Y', 'N']).default('Y'),
    carrierDisconnect: z.enum(['Y', 'N']).default('Y'),
  }),
  // The reference screen takes a file only; the reason decides what becomes of the old SIM.
  'swap-sim': z.object({ reasonId: objectId }),
  'swap-mdn': z.object({}),
  'order-fulfillment': z.object({}),
  // Manage Shipper Inventory, Bulk Upload: the type and Include Orders apply to every row.
  'shipper-inventory': z.object({
    esnType: z.enum(SHIPPER_ESN_TYPES, { required_error: 'Select ESN/SIM Type' }),
    includeOrders: z.enum(SHIPPER_INCLUDE_ORDERS, { required_error: 'Select Include Orders' }),
  }),
  'swap-mdn-by-customer': z.object({}),
  'mdn-topup': z.object({}),
  'customer-plan-change': z.object({}),
  hotline: z.object({ carrierId: objectId.nullable().default(null) }),
  unhotline: z.object({ carrierId: objectId.nullable().default(null) }),
  'deprovision-sim': z.object({
    remark: z
      .string({ required_error: 'Remark is required' })
      .trim()
      .min(1, 'Remark is required')
      .max(250),
  }),
  'print-label': z.object({
    shipper: z.enum(['UPS', 'USPS', 'FEDEX', 'ENDICIA', 'STAMPS', 'SHIPST'], {
      required_error: 'Select Shipper',
    }),
  }),
  'clear-customer-device': z.object({}),
  'customer-update': z.object({}),
  'import-customers': z.object({}),
  'nlad-process': z.object({}),
  'sim-device-information': z.object({}),
  'bulk-enrollment': z.object({}),
  'bulk-notes': z.object({}),
  // "Change SIM Status & Agent Name" (PC50): a status, an agent or both. A file row may carry
  // its own; the rule "at least one" is checked per row.
  'change-sim-status': z.object({
    status: z.enum(SIM_STATUSES).nullable().default(null),
    ownerUserId: objectId.nullable().default(null),
  }),
  'untag-device': z.object({}),
  // Update Inventory, ESN/SIM bulk upload: the page's Carrier applies to every SIM of the file.
  'update-sims': z.object({ carrierId: objectId.nullable().default(null) }),
  'update-devices': z.object({}),
  // Adjustments: "Single Inventory Re-assign" and "Single Device Re-assign".
  'adjust-sim': bulkOwner,
  'adjust-device': bulkOwner,
} satisfies Record<BulkOperation, z.ZodTypeAny>;

/** Header of the "Change SIM Status & Agent Name" file, as the reference's sample (lowercased). */
export const CHANGE_SIM_STATUS_CSV_COLUMNS = ['sim', 'agent id', 'status'] as const;
/** The reference's message when neither an agent nor a status is chosen (its own spelling). */
export const AGENT_OR_STATUS_MESSAGE = 'Please select atleast one either Agent ID OR Status';

/**
 * JSON body for "Type - 1" (paste). "Type - 2" (file) posts multipart form data with
 * `file` (CSV) and `params` (JSON string of the same params object).
 */
export const bulkPasteSchema = z.object({
  identifiers: z
    .array(z.string().trim().min(1).max(40))
    .min(1, 'Enter at least one value')
    .max(5000, 'At most 5000 values per batch'),
  params: z.record(z.unknown()).default({}),
});

export const bulkBatchListQuerySchema = listQuerySchema.extend({
  operation: z.enum(BULK_OPERATIONS).optional(),
});
export const bulkRowListQuerySchema = listQuerySchema.extend({
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  result: z.enum(['success', 'failure', 'pending']).optional(),
});

export interface BulkBatchDto {
  id: string;
  /** "Batch Name" on the reference reports. */
  name: string;
  operation: BulkOperation;
  inputType: (typeof BULK_INPUT_TYPES)[number];
  fileName: string | null;
  state: (typeof BULK_BATCH_STATES)[number];
  total: number;
  success: number;
  failure: number;
  uploadedAt: string;
  uploadedBy: UserRef | null;
  /** Label of the second step ("Send to Disconnect") while the batch waits for it. */
  sendLabel: string | null;
}

export interface BulkRowDto {
  id: string;
  line: number;
  key: string;
  /** `pending`: uploaded, waiting for the batch's second step. */
  result: 'success' | 'failure' | 'pending';
  message: string;
}

/** Column headers of the CSV accepted by the "add-units" bulk operation. */
export const ADD_UNITS_CSV_COLUMNS = [
  'carrier',
  'sim',
  'imei',
  'esim',
  'product_sku',
  'owner_login_id',
  'zip',
  'puk2',
  'po_number',
  'reserve_for_portin',
  'mdn',
] as const;

export const OWNER_LEVELS = USER_LEVELS.filter((l) => l !== 'admin');

/* ----------------------------- Update Inventory ----------------------------- */

/** "BYOD" of Update Inventory: SELECT HERE (no change), Y or N. */
export const BYOD_VALUES = ['Y', 'N'] as const;

const detailText = (max: number) =>
  z.preprocess(emptyToUndefined, z.string().trim().max(max).optional());
const detailCents = (label: string) =>
  z
    .number({ invalid_type_error: `${label} must be an amount` })
    .int()
    .min(0, `${label} cannot be negative`)
    .max(99_999_999)
    .optional();

/** Fields both forms have; an empty field keeps the stored value. */
const detailFields = {
  carrierId: z.preprocess(emptyToUndefined, objectId.optional()),
  modelId: z.preprocess(emptyToUndefined, objectId.optional()),
  poNumber: detailText(50),
  box: detailText(20),
  notes: detailText(30),
  wholesalePriceCents: detailCents('Wholesale Price'),
  byod: z.preprocess(emptyToUndefined, z.enum(BYOD_VALUES).optional()),
};

/** "ESN/SIM, Single Unit" of Update Inventory, with the reference's lengths (walk of 2026-10-15). */
export const simDetailsSchema = z.object({
  kind: z.literal('sim'),
  identifier: z.string().trim().min(1, 'ESN/SIM is required').max(25),
  ...detailFields,
  /** "MSL/PUK". */
  puk2: detailText(10),
  msid: detailText(10),
  sellingPriceCents: detailCents('Selling Price'),
  activationFeeCents: detailCents('Activation Fee'),
  uiccid: detailText(25),
  /** Tags this device to the SIM. */
  deviceId: detailText(25),
});

/** "DEVICE, Single Unit" of Update Inventory. */
export const deviceDetailsSchema = z.object({
  kind: z.literal('device'),
  identifier: z.string().trim().min(1, 'Device ID is required').max(25),
  ...detailFields,
  retailPriceCents: detailCents('Device Retail Price'),
  acpCoPayCents: detailCents('ACP Co-Pay Amount'),
  acpReimbursementCents: detailCents('ACP Device Reimbursement Amount'),
});

/** The note under each form, used as the message when nothing was filled. */
export const UPDATE_ONE_FIELD: Record<UnitKind, string> = {
  sim: 'Please update at least one field to update ESN/SIM.',
  device: 'Please update at least one field to update Device.',
};

export const unitDetailsSchema = z
  .discriminatedUnion('kind', [simDetailsSchema, deviceDetailsSchema])
  .superRefine((v, ctx) => {
    const { kind, identifier: _identifier, ...fields } = v;
    if (!Object.values(fields).some((x) => x !== undefined))
      ctx.addIssue({ code: 'custom', path: ['fields'], message: UPDATE_ONE_FIELD[kind] });
  });
export type UnitDetailsInput = z.input<typeof unitDetailsSchema>;
export type UnitDetailsData = z.output<typeof unitDetailsSchema>;

/** Names of the stored fields, as the forms label them; also used in row messages and history. */
export const UNIT_DETAIL_LABELS: Record<string, string> = {
  carrierId: 'Carrier',
  modelId: 'Model',
  puk2: 'MSL/PUK',
  msid: 'MSID',
  poNumber: 'PO',
  box: 'Box',
  wholesalePriceCents: 'Wholesale Price',
  sellingPriceCents: 'Selling Price',
  notes: 'Notes',
  uiccid: 'UICCID',
  activationFeeCents: 'Activation Fee',
  byod: 'BYOD',
  taggedWith: 'Device ID',
  retailPriceCents: 'Device Retail Price',
  acpCoPayCents: 'ACP Co-Pay Amount',
  acpReimbursementCents: 'ACP Device Reimbursement Amount',
};

/** Result of the single-unit form: the unit and the labels of what changed. */
export interface UnitDetailsResult {
  unit: UnitDto;
  changed: string[];
}

/** Headers of the two bulk files, as the reference's samples (lowercased). */
export const UPDATE_SIMS_CSV_COLUMNS = [
  'esn/sim',
  'model id',
  'msl/puk',
  'po#',
  'box',
  'notes',
  'wholesale price',
  'selling price',
  'activation fee',
  'byod (y/n)',
  'msid',
  'uiccid',
  'device id',
] as const;
export const UPDATE_DEVICES_CSV_COLUMNS = [
  'deviceid',
  'model id',
  'vendor',
  'po#',
  'box',
  'notes',
  'wholesale price',
  'carrier id',
  'shipper id',
  'device retail price',
  'byod (y/n)',
  'acp co-pay amount',
  'acp device reimbursement amount',
] as const;

/**
 * "Check ESN Compatibility" (Reporting > Check SIM Compatibility, PC270): "Enter your ESN
 * below:*", at most 25 characters (walk of 2026-10-23). The carrier's answer was not seen;
 * here it is compatible or not, with the carrier's reason.
 */
export const esnCompatibilitySchema = z.object({
  esn: z
    .string({ required_error: 'Enter your ESN' })
    .trim()
    .min(1, 'Enter your ESN')
    .max(25, 'At most 25 characters')
    .regex(/^[A-Za-z0-9]+$/, 'Only letters and digits'),
});
export type EsnCompatibilityInput = z.input<typeof esnCompatibilitySchema>;

export interface EsnCompatibilityDto {
  esn: string;
  compatible: boolean;
  message: string;
  /** The carrier's reference for the check. */
  reference: string;
}

/**
 * The stock counters of Reporting (PC19, walk of 2026-10-27, switch `reporting-2`), over the
 * caller's hierarchy. Assumption: a provisioned SIM is one that already has a telephone number.
 */
export interface StockCountsDto {
  simStock: number;
  simUsed: number;
  simFree: number;
  provisioned: number;
  notProvisioned: number;
  deviceStock: number;
  deviceUsed: number;
  deviceFree: number;
}
